/**
 * LifeScribe Admin Dashboard - MUI Theme Configuration
 * Creates Material-UI theme using design tokens
 * Based on LifeScribe Design System v4.0
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { colors, typography, spacing, shadows, borders } from './designTokens';

/**
 * Creates MUI theme for given mode (light/dark)
 */
export const createAppTheme = (mode: 'light' | 'dark') => {
  const themeOptions: ThemeOptions = {
    palette: {
      mode,
      primary: {
        main: colors.deepTeal[500],
        light: colors.deepTeal[400],
        dark: colors.deepTeal[700],
        contrastText: colors.white,
      },
      secondary: {
        main: colors.warmGold[500],
        light: colors.warmGold[400],
        dark: colors.warmGold[700],
        contrastText: colors.gray[900],
      },
      success: {
        main: colors.success.main,
        light: colors.success.light,
        dark: colors.success.dark,
        contrastText: colors.white,
      },
      warning: {
        main: colors.warning.main,
        light: colors.warning.light,
        dark: colors.warning.dark,
        contrastText: colors.gray[900],
      },
      error: {
        main: colors.error.main,
        light: colors.error.light,
        dark: colors.error.dark,
        contrastText: colors.white,
      },
      info: {
        main: colors.info.main,
        light: colors.info.light,
        dark: colors.info.dark,
        contrastText: colors.white,
      },
      background: {
        default: mode === 'light' ? colors.softCream[200] : colors.gray[900],
        paper: mode === 'light' ? colors.white : colors.gray[800],
      },
      text: {
        primary: mode === 'light' ? colors.gray[900] : colors.gray[50],
        secondary: mode === 'light' ? colors.gray[600] : colors.gray[400],
        disabled: mode === 'light' ? colors.gray[400] : colors.gray[600],
      },
      divider: mode === 'light' ? colors.gray[200] : colors.gray[700],
      action: {
        active: mode === 'light' ? colors.gray[600] : colors.gray[300],
        hover: mode === 'light' ? colors.gray[100] : colors.gray[700],
        selected: mode === 'light' ? colors.gray[200] : colors.gray[600],
        disabled: mode === 'light' ? colors.gray[300] : colors.gray[700],
        disabledBackground: mode === 'light' ? colors.gray[100] : colors.gray[800],
      },
    },

    typography: {
      fontFamily: typography.fonts.body,
      fontSize: 16,
      fontWeightLight: typography.weights.light,
      fontWeightRegular: typography.weights.normal,
      fontWeightMedium: typography.weights.medium,
      fontWeightBold: typography.weights.bold,

      h1: {
        fontFamily: typography.fonts.display,
        fontSize: typography.sizes['5xl'],
        fontWeight: typography.weights.bold,
        lineHeight: typography.lineHeights.tight,
        letterSpacing: typography.letterSpacing.tight,
      },
      h2: {
        fontFamily: typography.fonts.display,
        fontSize: typography.sizes['4xl'],
        fontWeight: typography.weights.bold,
        lineHeight: typography.lineHeights.tight,
        letterSpacing: typography.letterSpacing.tight,
      },
      h3: {
        fontFamily: typography.fonts.heading,
        fontSize: typography.sizes['3xl'],
        fontWeight: typography.weights.semibold,
        lineHeight: typography.lineHeights.normal,
        letterSpacing: typography.letterSpacing.normal,
      },
      h4: {
        fontFamily: typography.fonts.heading,
        fontSize: typography.sizes['2xl'],
        fontWeight: typography.weights.semibold,
        lineHeight: typography.lineHeights.normal,
        letterSpacing: typography.letterSpacing.normal,
      },
      h5: {
        fontFamily: typography.fonts.heading,
        fontSize: typography.sizes.xl,
        fontWeight: typography.weights.medium,
        lineHeight: typography.lineHeights.normal,
        letterSpacing: typography.letterSpacing.normal,
      },
      h6: {
        fontFamily: typography.fonts.heading,
        fontSize: typography.sizes.lg,
        fontWeight: typography.weights.medium,
        lineHeight: typography.lineHeights.normal,
        letterSpacing: typography.letterSpacing.normal,
      },
      subtitle1: {
        fontFamily: typography.fonts.body,
        fontSize: typography.sizes.base,
        fontWeight: typography.weights.medium,
        lineHeight: typography.lineHeights.normal,
      },
      subtitle2: {
        fontFamily: typography.fonts.body,
        fontSize: typography.sizes.sm,
        fontWeight: typography.weights.medium,
        lineHeight: typography.lineHeights.normal,
      },
      body1: {
        fontFamily: typography.fonts.body,
        fontSize: typography.sizes.base,
        fontWeight: typography.weights.normal,
        lineHeight: typography.lineHeights.relaxed,
        letterSpacing: typography.letterSpacing.normal,
      },
      body2: {
        fontFamily: typography.fonts.body,
        fontSize: typography.sizes.sm,
        fontWeight: typography.weights.normal,
        lineHeight: typography.lineHeights.normal,
        letterSpacing: typography.letterSpacing.normal,
      },
      button: {
        fontFamily: typography.fonts.heading,
        fontSize: typography.sizes.base,
        fontWeight: typography.weights.semibold,
        lineHeight: typography.lineHeights.normal,
        letterSpacing: typography.letterSpacing.wide,
        textTransform: 'none',
      },
      caption: {
        fontFamily: typography.fonts.body,
        fontSize: typography.sizes.xs,
        fontWeight: typography.weights.normal,
        lineHeight: typography.lineHeights.normal,
        letterSpacing: typography.letterSpacing.normal,
      },
      overline: {
        fontFamily: typography.fonts.heading,
        fontSize: typography.sizes.xs,
        fontWeight: typography.weights.semibold,
        lineHeight: typography.lineHeights.normal,
        letterSpacing: typography.letterSpacing.widest,
        textTransform: 'uppercase',
      },
    },

    spacing: 8, // Base spacing unit (1 = 8px)

    shape: {
      borderRadius: parseInt(borders.radius.lg), // 12px default
    },

    shadows: [
      shadows.none,
      shadows.xs,
      shadows.sm,
      shadows.sm,
      shadows.md,
      shadows.md,
      shadows.md,
      shadows.lg,
      shadows.lg,
      shadows.lg,
      shadows.xl,
      shadows.xl,
      shadows.xl,
      shadows['2xl'],
      shadows['2xl'],
      shadows['2xl'],
      shadows['2xl'],
      shadows['2xl'],
      shadows['2xl'],
      shadows['2xl'],
      shadows['2xl'],
      shadows['2xl'],
      shadows['2xl'],
      shadows['2xl'],
      shadows['2xl'],
    ],

    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: false,
        },
        styleOverrides: {
          root: {
            borderRadius: borders.radius.lg,
            padding: `${spacing[3]} ${spacing[6]}`,
            fontSize: typography.sizes.base,
            fontWeight: typography.weights.semibold,
            textTransform: 'none',
            transition: `all 200ms ease-in-out`,
          },
          contained: {
            boxShadow: shadows.sm,
            '&:hover': {
              boxShadow: shadows.md,
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          },
          outlined: {
            borderWidth: borders.width.medium,
            '&:hover': {
              borderWidth: borders.width.medium,
            },
          },
          sizeSmall: {
            padding: `${spacing[2]} ${spacing[4]}`,
            fontSize: typography.sizes.sm,
          },
          sizeLarge: {
            padding: `${spacing[4]} ${spacing[8]}`,
            fontSize: typography.sizes.lg,
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.xl,
            boxShadow: shadows.sm,
            transition: `all 200ms ease-in-out`,
            '&:hover': {
              boxShadow: shadows.md,
            },
          },
        },
      },

      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: spacing[6],
          },
          title: {
            fontFamily: typography.fonts.heading,
            fontSize: typography.sizes.xl,
            fontWeight: typography.weights.semibold,
          },
          subheader: {
            fontSize: typography.sizes.sm,
            marginTop: spacing[1],
          },
        },
      },

      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: spacing[6],
            '&:last-child': {
              paddingBottom: spacing[6],
            },
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.lg,
          },
          elevation1: {
            boxShadow: shadows.sm,
          },
          elevation2: {
            boxShadow: shadows.md,
          },
          elevation3: {
            boxShadow: shadows.lg,
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.full,
            fontWeight: typography.weights.medium,
            fontSize: typography.sizes.sm,
          },
          sizeSmall: {
            fontSize: typography.sizes.xs,
          },
        },
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: borders.radius.lg,
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.lg,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.deepTeal[400],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: borders.width.medium,
            },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontWeight: typography.weights.medium,
          },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? colors.gray[50] : colors.gray[800],
            '& .MuiTableCell-head': {
              fontFamily: typography.fonts.heading,
              fontWeight: typography.weights.semibold,
              fontSize: typography.sizes.sm,
              textTransform: 'uppercase',
              letterSpacing: typography.letterSpacing.wider,
              color: mode === 'light' ? colors.gray[700] : colors.gray[300],
            },
          },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'light' ? colors.deepTeal[50] : colors.gray[700],
            },
            '&.MuiTableRow-hover:hover': {
              backgroundColor: mode === 'light' ? colors.deepTeal[50] : colors.gray[700],
            },
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: mode === 'light' ? colors.gray[200] : colors.gray[700],
            padding: spacing[4],
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.md,
            transition: `all 150ms ease-in-out`,
            '&:hover': {
              backgroundColor: mode === 'light' ? colors.gray[100] : colors.gray[700],
            },
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'light' ? colors.gray[900] : colors.gray[700],
            fontSize: typography.sizes.sm,
            padding: `${spacing[2]} ${spacing[3]}`,
            borderRadius: borders.radius.md,
          },
          arrow: {
            color: mode === 'light' ? colors.gray[900] : colors.gray[700],
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: borders.radius.xl,
            boxShadow: shadows.xl,
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: typography.fonts.heading,
            fontSize: typography.sizes['2xl'],
            fontWeight: typography.weights.semibold,
            padding: spacing[6],
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: spacing[6],
          },
        },
      },

      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: spacing[6],
            gap: spacing[3],
          },
        },
      },

      MuiSnackbar: {
        styleOverrides: {
          root: {
            '& .MuiAlert-root': {
              borderRadius: borders.radius.lg,
              boxShadow: shadows.lg,
            },
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.lg,
            padding: `${spacing[3]} ${spacing[4]}`,
          },
          standardSuccess: {
            backgroundColor: colors.success[50],
            color: colors.success.dark,
            '& .MuiAlert-icon': {
              color: colors.success.main,
            },
          },
          standardWarning: {
            backgroundColor: colors.warning[50],
            color: colors.warning.dark,
            '& .MuiAlert-icon': {
              color: colors.warning.main,
            },
          },
          standardError: {
            backgroundColor: colors.error[50],
            color: colors.error.dark,
            '& .MuiAlert-icon': {
              color: colors.error.main,
            },
          },
          standardInfo: {
            backgroundColor: colors.info[50],
            color: colors.info.dark,
            '& .MuiAlert-icon': {
              color: colors.info.main,
            },
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: {
            borderBottom: `${borders.width.thin} solid ${mode === 'light' ? colors.gray[200] : colors.gray[700]}`,
          },
          indicator: {
            height: borders.width.thick,
            borderRadius: `${borders.radius.sm} ${borders.radius.sm} 0 0`,
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: typography.fonts.heading,
            fontWeight: typography.weights.medium,
            fontSize: typography.sizes.base,
            textTransform: 'none',
            minHeight: 48,
            padding: `${spacing[3]} ${spacing[4]}`,
            '&.Mui-selected': {
              fontWeight: typography.weights.semibold,
            },
          },
        },
      },

      MuiBreadcrumbs: {
        styleOverrides: {
          separator: {
            marginLeft: spacing[2],
            marginRight: spacing[2],
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: mode === 'light' ? colors.gray[200] : colors.gray[700],
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.lg,
            marginBottom: spacing[1],
            '&:hover': {
              backgroundColor: mode === 'light' ? colors.gray[100] : colors.gray[700],
            },
            '&.Mui-selected': {
              backgroundColor: mode === 'light' ? colors.deepTeal[50] : colors.gray[700],
              color: colors.deepTeal[700],
              '&:hover': {
                backgroundColor: mode === 'light' ? colors.deepTeal[100] : colors.gray[600],
              },
            },
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};
